import { ReactNode, useEffect } from "react";
import { Alert, Col, Form, Row } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCircleExclamation } from "@fortawesome/free-solid-svg-icons";
import { Controller, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { ModalCustom } from "components/ModalCustom";
import { Required } from "components/Required";
import FileUpload from "components/UploadArquivos";
import { CustomDatePicker } from "components/DatePicker";
import { useMaskInput } from "hooks/useMaskInput";
import { baixaManualSchema, BaixaManualForm, parseValorPago } from "./validator";
import { Contador, ErrorMessage } from "./style";

const MAX_OBSERVACAO = 200;

const DEFAULT_VALUES = {
  tipo: undefined as "cancelado" | "manual" | undefined,
  dataPagamento: null as Date | null,
  valorPago: "",
  comprovante: null as File | null,
  observacao: "",
};

interface IProps {
  show: boolean;
  titulo: string;
  subTitulo?: string;
  alertaManual?: ReactNode;
  handleClose: () => void;
  onConfirmarManual: (
    dataPagamento: string,
    comprovante: File,
    observacao: string,
    valorPago: number,
  ) => void;
  onCancelar: (observacao: string) => void;
}

export function ModalBaixaManual({
  show,
  titulo,
  subTitulo = "Preencha as informações para dar baixa no débito selecionado.",
  alertaManual,
  handleClose,
  onConfirmarManual,
  onCancelar,
}: IProps) {
  const {
    handleSubmit,
    control,
    reset,
    watch,
    formState: { errors },
  } = useForm<BaixaManualForm>({
    resolver: yupResolver(baixaManualSchema),
    defaultValues: DEFAULT_VALUES,
  });

  const { mInputCoin2 } = useMaskInput();
  const tipo = watch("tipo");
  const observacao = watch("observacao") ?? "";

  useEffect(() => {
    reset(DEFAULT_VALUES);
  }, [show]);

  function handleClose_() {
    reset(DEFAULT_VALUES);
    handleClose();
  }

  const onValid = (dados: BaixaManualForm) => {
    if (dados.tipo === "cancelado") {
      onCancelar(dados.observacao);
      handleClose_();
      return;
    }

    const data = dados.dataPagamento as Date;
    const pad = (n: number) => String(n).padStart(2, "0");
    const dataFormatada = `${data.getFullYear()}-${pad(data.getMonth() + 1)}-${pad(data.getDate())}`;
    onConfirmarManual(
      dataFormatada,
      dados.comprovante as File,
      dados.observacao,
      parseValorPago(dados.valorPago),
    );
    handleClose_();
  };

  return (
    <ModalCustom
      show={show}
      handleClose={handleClose_}
      title={titulo}
      subTitle={subTitulo}
      centered
      footer
      footerConfirmButton
      onConfirm={handleSubmit(onValid)}
      confirmButtonText="Salvar"
      size="lg"
    >
      <Form>
        <Controller
          name="tipo"
          control={control}
          render={({ field }) => (
            <div className="d-flex gap-4 mb-2">
              <Form.Check
                type="radio"
                id="baixa-pgto-cancelado"
                label="Pgto cancelado"
                checked={field.value === "cancelado"}
                onChange={() => field.onChange("cancelado")}
              />
              <Form.Check
                type="radio"
                id="baixa-pgto-manual"
                label="Pgto manual"
                checked={field.value === "manual"}
                onChange={() => field.onChange("manual")}
              />
            </div>
          )}
        />
        {!!errors.tipo && <ErrorMessage>{errors.tipo.message}</ErrorMessage>}

        {tipo === "manual" && alertaManual && (
          <Alert
            variant="warning"
            className="mt-3 mb-0 py-2 d-flex align-items-center gap-2"
          >
            <FontAwesomeIcon
              icon={faCircleExclamation}
              style={{ color: "#e67e22", fontSize: 16 }}
            />
            <span>{alertaManual}</span>
          </Alert>
        )}

        {tipo === "manual" && (
          <>
            <Row className="mt-3">
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>
                    Data do pagamento <Required />
                  </Form.Label>
                  <Controller
                    name="dataPagamento"
                    control={control}
                    render={({ field }) => (
                      <CustomDatePicker
                        selected={field.value}
                        maxDate={new Date()}
                        mensagemErro={errors.dataPagamento?.message as any}
                        onChange={(date) => field.onChange(date)}
                      />
                    )}
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>
                    Valor pago <Required />
                  </Form.Label>
                  <Controller
                    name="valorPago"
                    control={control}
                    render={({ field }) => (
                      <Form.Control
                        type="text"
                        placeholder="R$ 0,00"
                        value={field.value ?? ""}
                        onChange={(e) => field.onChange(mInputCoin2(e))}
                      />
                    )}
                  />
                  {!!errors.valorPago && (
                    <ErrorMessage>{errors.valorPago.message}</ErrorMessage>
                  )}
                </Form.Group>
              </Col>
            </Row>

            <Form.Group className="mb-3">
              <Form.Label>
                Comprovante de pagamento <Required />
              </Form.Label>
              <Controller
                name="comprovante"
                control={control}
                render={({ field: { onChange } }) => (
                  <FileUpload
                    onFileUpload={(file) => onChange(file)}
                    mensagemErro={errors.comprovante?.message as any}
                    comMaxWidth={false}
                    inputAccept=".pdf,.jpg,.jpeg,.png"
                  />
                )}
              />
            </Form.Group>
          </>
        )}

        {!!tipo && (
          <Form.Group className="mb-3 mt-3">
            <Form.Label>
              Observação <Required />
            </Form.Label>
            <Controller
              name="observacao"
              control={control}
              render={({ field }) => (
                <Form.Control
                  as="textarea"
                  rows={3}
                  maxLength={MAX_OBSERVACAO}
                  placeholder="Observação"
                  {...field}
                />
              )}
            />
            <Contador>
              {observacao.length}/{MAX_OBSERVACAO}
            </Contador>
            {!!errors.observacao && (
              <ErrorMessage>{errors.observacao.message}</ErrorMessage>
            )}
          </Form.Group>
        )}
      </Form>
    </ModalCustom>
  );
}
